import React, { useMemo } from 'react';
import { useRequest } from 'ahooks';
import { Spin } from 'antd';
import dayjs from 'dayjs';
import { keepTwoOrLine } from '@/utils/utils';
import { getReportTimeRange, ReportType } from '../../const';
import CardBox from '../CardBox';
import PieCard from '../PieCard';
import RankingCard from '../RankingCard';
import {
  apiV2ReportDataEnergyReportPeakValleyRatioPost,
  apiV2ReportDataEnergyReportAlarmTypeDistributionPost,
  apiV2ReportDataElectricityDemandPost,
  apiV2ReportDataElectricityPeakLoadRatePost,
  apiV2ReportDataElectricityTransformerLoadTop5Post,
} from '@maxtropy/device-customer-apis-v2';
import iconShip from '../../imgs/icon_ship.png';
import spinCircleWrapper from '../../imgs/spin_circle_wrapper.png';
import monthIcon from '../../imgs/month_icon.png';
import dayIcon from '../../imgs/day_icon.png';
import elecCardBg from '../../imgs/elec_card_bg.png';
import styles from './index.module.scss';
import type { Dayjs } from 'dayjs';

interface ElectricityAnalysisProps {
  reportType: ReportType;
  selectedDate: Dayjs;
}

const ElectricityAnalysis: React.FC<ElectricityAnalysisProps> = ({ reportType, selectedDate }) => {
  // 计算时间范围
  const timeRange = useMemo(() => getReportTimeRange(reportType, selectedDate), [reportType, selectedDate]);

  // 统计粒度
  const timeResolution = useMemo(() => (reportType === ReportType.DAILY ? 'DAY' : 'MONTH'), [reportType]);

  // 峰谷占比接口
  const { data: peakValleyData, loading: peakValleyLoading } = useRequest(
    async () => {
      const res = await apiV2ReportDataEnergyReportPeakValleyRatioPost({
        fromTime: timeRange.fromTime,
        toTime: timeRange.toTime,
        timeResolution,
      });
      return res;
    },
    {
      refreshDeps: [timeRange, timeResolution],
    }
  );

  // 报警类型分布接口
  const { data: alarmTypeData, loading: alarmTypeLoading } = useRequest(
    async () => {
      const res = await apiV2ReportDataEnergyReportAlarmTypeDistributionPost({
        fromTime: timeRange.fromTime,
        toTime: timeRange.toTime,
        timeResolution,
      });
      return res;
    },
    {
      refreshDeps: [timeRange, timeResolution],
    }
  );

  // 最大需量接口
  const { data: demandData, loading: demandLoading } = useRequest(
    async () => {
      const res = await apiV2ReportDataElectricityDemandPost({
        fromTime: timeRange.fromTime,
        toTime: timeRange.toTime,
        timeResolution,
      });
      return res;
    },
    {
      refreshDeps: [timeRange, timeResolution],
    }
  );

  // 峰值负载率接口
  const { data: peakLoadRateData, loading: peakLoadRateLoading } = useRequest(
    async () => {
      const res = await apiV2ReportDataElectricityPeakLoadRatePost({
        fromTime: timeRange.fromTime,
        toTime: timeRange.toTime,
        timeResolution,
      });
      return res;
    },
    {
      refreshDeps: [timeRange, timeResolution],
    }
  );

  // 变压器负载率排名接口
  const { data: transformerData, loading: transformerLoading } = useRequest(
    async () => {
      const res = await apiV2ReportDataElectricityTransformerLoadTop5Post({
        fromTime: timeRange.fromTime,
        toTime: timeRange.toTime,
        timeResolution,
      });
      return res;
    },
    {
      refreshDeps: [timeRange, timeResolution],
    }
  );

  // 月度最大需量数据
  const monthlyDemandLines = useMemo(() => {
    const list = demandData?.monthlyDemandList ?? [];
    return list.map(item => ({
      name: item.circuitName ?? '--',
      value: item.demandValue ?? 0,
      time: item.demandTs ? dayjs(item.demandTs).format('YYYY-MM-DD HH:mm:ss') : '--',
    }));
  }, [demandData]);

  // 日度最大需量数据
  const dailyDemandLines = useMemo(() => {
    const list = demandData?.dailyDemandList ?? [];
    return list.map(item => ({
      name: item.circuitName ?? '--',
      value: item.demandValue ?? 0,
      time: item.demandTs ? dayjs(item.demandTs).format('HH:mm:ss') : '--',
    }));
  }, [demandData]);

  // 月度峰值负载率数据
  const monthlyLoadRates = useMemo(() => {
    const list = peakLoadRateData?.monthlyPeakLoadRateList ?? [];
    return list.map(item => ({
      name: item.circuitName ?? '--',
      value: item.peakLoadRate ?? 0,
      time: item.peakLoadRateTs ? dayjs(item.peakLoadRateTs).format('YYYY-MM-DD HH:mm:ss') : '--',
    }));
  }, [peakLoadRateData]);

  // 日度峰值负载率数据
  const dailyLoadRates = useMemo(() => {
    const list = peakLoadRateData?.dailyPeakLoadRateList ?? [];
    return list.map(item => ({
      name: item.circuitName ?? '--',
      value: item.peakLoadRate ?? 0,
      time: item.peakLoadRateTs ? dayjs(item.peakLoadRateTs).format('HH:mm:ss') : '--',
    }));
  }, [peakLoadRateData]);

  // 峰谷占比 PieCard 数据
  const peakValleyPieData = useMemo(() => {
    const colors = ['#FF6D2C', '#CE90D1', '#0DB6D9', '#63BC7F'];
    const periods = peakValleyData?.periods ?? [];

    // 计算尖、峰、平的占比总和（不包括"谷"）
    const otherRatioSum = periods
      .filter(item => !item.periodName?.includes('谷'))
      .reduce((sum, item) => sum + (item.ratio ?? 0), 0);

    return periods.map((item, i) => {
      // 如果是"谷"，占比用 100% 减去尖峰平的占比
      const isGu = item.periodName?.includes('谷');
      const rate = isGu ? Math.max(0, 100 - otherRatioSum) : item.ratio ?? 0;

      return {
        name: item.periodName ?? '--',
        value: item.consumption ?? 0,
        rate,
        color: colors[i % colors.length],
      };
    });
  }, [peakValleyData]);

  // 报警类型分布 PieCard 数据
  const alarmPieData = useMemo(() => {
    const colors = ['#FF6D2C', '#FFCB47', '#00ADFF', '#16DD8E', '#ABD335'];
    const list = alarmTypeData?.list ?? [];
    return list.map((item, i) => ({
      name: item.alarmType ?? '--',
      value: item.count ?? 0,
      rate: item.ratio ?? 0,
      color: colors[i % colors.length],
    }));
  }, [alarmTypeData]);

  // 变压器负载率排名数据
  const transformerRankingData = useMemo(() => {
    const list = transformerData?.transformerList ?? [];
    const maxLoadRate = Math.max(...list.map(r => r.maxLoadRate ?? 0));
    return list.map(item => ({
      name: item.name ?? '--',
      percent: Math.round(((item.maxLoadRate ?? 0) / maxLoadRate) * 100),
      value: item.maxLoadRate ?? 0,
      barColor: getLoadRateColor(item.maxLoadRate ?? 0),
    }));
  }, [transformerData]);

  return (
    <CardBox title="用电分析">
      {/* 第一行：最大需量（左）+ 峰值负载率（右） */}
      <div className={styles.first_row}>
        {/* 左半部分：月度最大需量（左）+ 日度最大需量（右，仅日报显示），中间虚线分割 */}
        <div
          className={styles.demand_section}
          style={{
            backgroundImage: `url(${elecCardBg})`,
            flexDirection: reportType === ReportType.DAILY ? 'row' : 'column',
          }}
        >
          {/* 月度最大需量 */}
          <div className={reportType === ReportType.DAILY ? styles.demand_left : styles.demand_full}>
            <div className={styles.demand_header}>
              <div className={styles.icon_wrapper}>
                <img className={styles.spin_icon} src={spinCircleWrapper} alt="" />
                <img className={styles.static_icon} src={monthIcon} alt="" />
              </div>
              <span className={styles.demand_title}>月度最大需量</span>
            </div>
            <Spin spinning={demandLoading}>
              <div className={styles.demand_lines}>
                {monthlyDemandLines.map(line => (
                  <div key={line.name} className={styles.demand_line_item}>
                    <div className={styles.demand_line_header}>
                      <img className={styles.ship_icon} src={iconShip} alt="" />
                      <span className={styles.line_name}>{line.name}</span>
                      <span className={styles.line_value}>{keepTwoOrLine(line.value)} kW</span>
                    </div>
                    <div className={styles.line_time}>{line.time}</div>
                  </div>
                ))}
              </div>
            </Spin>
          </div>
          {/* 虚线分割（仅日报显示） */}
          {reportType === ReportType.DAILY && <div className={styles.vertical_divider} />}
          {/* 日度最大需量（仅日报显示） */}
          {reportType === ReportType.DAILY && (
            <div className={styles.demand_right}>
              <div className={styles.demand_header}>
                <div className={styles.icon_wrapper}>
                  <img className={styles.spin_icon} src={spinCircleWrapper} alt="" />
                  <img className={styles.static_icon} src={dayIcon} alt="" />
                </div>
                <span className={styles.demand_title}>日度最大需量</span>
              </div>
              <Spin spinning={demandLoading}>
                <div className={styles.demand_lines}>
                  {dailyDemandLines.map(line => (
                    <div key={line.name} className={styles.demand_line_item}>
                      <div className={styles.demand_line_header}>
                        <img className={styles.ship_icon} src={iconShip} alt="" />
                        <span className={styles.line_name}>{line.name}</span>
                        <span className={styles.line_value}>{keepTwoOrLine(line.value)} kW</span>
                      </div>
                      <div className={styles.line_time}>{line.time}</div>
                    </div>
                  ))}
                </div>
              </Spin>
            </div>
          )}
        </div>

        {/* 右半部分：月度峰值负载率（左）+ 本日峰值负载率（右，仅日报显示），中间虚线分割 */}
        <div
          className={styles.load_rate_section}
          style={{
            backgroundImage: `url(${elecCardBg})`,
            flexDirection: reportType === ReportType.DAILY ? 'row' : 'column',
          }}
        >
          {/* 月度35kV总变峰值负载率 */}
          <div className={reportType === ReportType.DAILY ? styles.load_rate_left : styles.load_rate_full}>
            <div className={styles.demand_header}>
              <div className={styles.icon_wrapper}>
                <img className={styles.spin_icon} src={spinCircleWrapper} alt="" />
                <img className={styles.static_icon} src={monthIcon} alt="" />
              </div>
              <span className={styles.demand_title}>月度35kV总变峰值负载率</span>
            </div>
            <Spin spinning={peakLoadRateLoading}>
              <div className={styles.demand_lines}>
                {monthlyLoadRates.map(line => (
                  <div key={line.name} className={styles.demand_line_item}>
                    <div className={styles.demand_line_header}>
                      <img className={styles.ship_icon} src={iconShip} alt="" />
                      <span className={styles.line_name}>{line.name}</span>
                      <span className={styles.line_value_percent}>{keepTwoOrLine(line.value)}%</span>
                    </div>
                    <div className={styles.line_time}>{line.time}</div>
                  </div>
                ))}
              </div>
            </Spin>
          </div>
          {/* 虚线分割（仅日报显示） */}
          {reportType === ReportType.DAILY && <div className={styles.vertical_divider} />}
          {/* 本日35kV总变峰值负载率（仅日报显示） */}
          {reportType === ReportType.DAILY && (
            <div className={styles.load_rate_right}>
              <div className={styles.demand_header}>
                <div className={styles.icon_wrapper}>
                  <img className={styles.spin_icon} src={spinCircleWrapper} alt="" />
                  <img className={styles.static_icon} src={dayIcon} alt="" />
                </div>
                <span className={styles.demand_title}>本日35kV总变峰值负载率</span>
              </div>
              <Spin spinning={peakLoadRateLoading}>
                <div className={styles.demand_lines}>
                  {dailyLoadRates.map(line => (
                    <div key={line.name} className={styles.demand_line_item}>
                      <div className={styles.demand_line_header}>
                        <img className={styles.ship_icon} src={iconShip} alt="" />
                        <span className={styles.line_name}>{line.name}</span>
                        <span className={styles.line_value_percent}>{keepTwoOrLine(line.value)}%</span>
                      </div>
                      <div className={styles.line_time}>{line.time}</div>
                    </div>
                  ))}
                </div>
              </Spin>
            </div>
          )}
        </div>
      </div>

      {/* 第二行：全厂峰谷占比 + 报警类型分布 + 变压器负载率排名，平均分 */}
      <div className={styles.main_row}>
        {/* 全厂峰谷占比 */}
        <div className={styles.equal_col}>
          <PieCard
            title="全厂峰谷占比"
            data={peakValleyPieData}
            unit="kWh"
            centerLabel="kWh"
            loading={peakValleyLoading}
          />
        </div>

        {/* 报警类型分布 */}
        <div className={styles.equal_col}>
          <PieCard
            title="报警类型分布"
            data={alarmPieData}
            unit="次"
            centerLabel="总计"
            loading={alarmTypeLoading}
            integerValue={true}
          />
        </div>

        {/* 变压器负载率排名 */}
        <div className={styles.equal_col}>
          <RankingCard
            title="变压器负载率排名"
            data={transformerRankingData}
            barColor="#16DD8E"
            showValue={true}
            columnHeaders={{ rank: '排名', name: '变压器名称', value: '最大负载率' }}
            valueFormatter={v => `${keepTwoOrLine(v)}%`}
            dynamicBarColor={true}
          />
        </div>
      </div>
    </CardBox>
  );
};

/** 根据负载率获取颜色 */
function getLoadRateColor(loadRate: number): string {
  if (loadRate >= 100) return '#FF9044';
  if (loadRate >= 80) return '#FF9247';
  if (loadRate >= 60) return '#FFCB47';
  return '#16DD8E';
}

export default ElectricityAnalysis;
